import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, Plus, ChevronLeft, ChevronRight, X, Eye } from "lucide-react";
import { get } from "@/lib/apiClient";
import { API_URL } from "@/_config";
import { useAuth } from "@/context/AuthContext";
import { useFreshdeskWidget } from "@/hooks/useFreshdeskWidget";

interface SiteSettings {
  support_phone: string;
  support_address: string;
  support_maps_embed_url: string;
}

interface SupportTicket {
  id: string;
  ticket_number: string | null;
  subject: string;
  body: string;
  attachments: string[];
  status: "open" | "in_progress" | "closed";
  created_at: string;
}


export default function SupportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useFreshdeskWidget(user ?? null);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const showPhone = !!(user?.organization?.plan_features?.campaign_features);

  const { data: settingsData } = useQuery<{ data: SiteSettings }>({
    queryKey: ["site-settings-support"],
    queryFn: () => get<SiteSettings>(API_URL.SUPPORT.SITE_SETTINGS),
    staleTime: 1000 * 60 * 10,
  });
  const settings = settingsData?.data;

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ["support-tickets", page],
    queryFn: () =>
      get<{ tickets: SupportTicket[] }>(API_URL.SUPPORT.TICKETS, {
        params: { page, per_page: 15 },
      }),
  });

  const { data: ticketDetailData } = useQuery({
    queryKey: ["support-ticket", selectedId],
    queryFn: () => get<SupportTicket>(API_URL.SUPPORT.TICKET(selectedId!)),
    enabled: !!selectedId,
  });

  const tickets: SupportTicket[] = (ticketsData as any)?.data?.tickets ?? [];
  const lastPage = (ticketsData as any)?.meta?.last_page ?? 1;
  const total = (ticketsData as any)?.meta?.total ?? 0;
  const selectedTicket: SupportTicket | null = (ticketDetailData as any)?.data ?? null;

  return (
    <div className="sp-page">
      <div className="sp-header">
        <div>
          <h1 className="sp-title">Support</h1>
          <p className="sp-subtitle">Contact our team or view your support requests.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/app/support/new")}>
          <Plus size={16} /> Create Support Request
        </button>
      </div>

      {/* Contact Info Card */}
      <div className="sp-card">
        <div className="sp-contact-body">
          {settings?.support_address && (
            <div className="sp-map-wrap">
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent(settings.support_address)}&output=embed&z=16`}
                width="100%"
                height="200"
                style={{ border: 0, display: "block" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Support office location"
              />
              <div className="sp-map-pin">
                <MapPin size={32} strokeWidth={2} fill="#ef4444" color="#fff" />
              </div>
            </div>
          )}

          <div className="sp-contact-details">
            <h2 className="sp-contact-title">Contact Us</h2>
            <div className="sp-contact-row">
              <MapPin size={16} className="sp-contact-icon" />
              <div>
                <div className="sp-contact-label">Address</div>
                <div className="sp-contact-value">
                  {settings?.support_address || "Calgary, Alberta, Canada"}
                </div>
              </div>
            </div>
            {showPhone && settings?.support_phone ? (
              <div className="sp-contact-row">
                <Phone size={16} className="sp-contact-icon" />
                <div>
                  <div className="sp-contact-label">Phone Support</div>
                  <a href={`tel:${settings.support_phone}`} className="sp-contact-value" style={{ color: "var(--primary)", textDecoration: "none", fontWeight: 500 }}>
                    {settings.support_phone}
                  </a>
                  <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 2 }}>
                    Available for Gold &amp; Platinum plan members
                  </div>
                </div>
              </div>
            ) : (
              <div className="sp-contact-row">
                <Phone size={16} className="sp-contact-icon--muted" />
                <div>
                  <div className="sp-contact-label" style={{ color: "var(--gray-300)" }}>Phone Support</div>
                  <div style={{ fontSize: 13, color: "var(--gray-400)" }}>
                    Available on Gold &amp; Platinum plans.{" "}
                    <button
                      onClick={() => navigate("/app/settings?tab=Billing")}
                      style={{ background: "none", border: "none", color: "var(--primary)", cursor: "pointer", padding: 0, fontSize: 13, textDecoration: "underline" }}
                    >
                      Upgrade
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tickets list */}
      <div className="sp-card">
        <div className="sp-card-header">
          <h2 className="sp-card-title">
            Your Support Requests
            {total > 0 && <span className="sp-card-title-count">({total})</span>}
          </h2>
        </div>

        {isLoading ? (
          <div className="sp-empty" style={{ color: "var(--gray-400)" }}>Loading…</div>
        ) : tickets.length === 0 ? (
          <div className="sp-empty">
            <div className="sp-empty-icon">🎫</div>
            <div className="sp-empty-title">No support requests yet</div>
            <div className="sp-empty-text">
              Have a question? Create a support request and we'll get back to you.
            </div>
            <button className="btn btn-primary" onClick={() => navigate("/app/support/new")}>
              Create Support Request
            </button>
          </div>
        ) : (
          <>
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Subject</th>
                  <th>Submitted</th>
                  <th className="sp-th-action" />
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id} onClick={() => setSelectedId(ticket.id)}>
                    <td className="sp-td-ticket">{ticket.ticket_number ?? "—"}</td>
                    <td style={{ fontWeight: 500 }}>{ticket.subject}</td>
                    <td className="sp-td-date">
                      {new Date(ticket.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
                    </td>
                    <td className="sp-td-action">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedId(ticket.id); }}
                        title="View details"
                        className="btn-icon"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {lastPage > 1 && (
              <div className="sp-pagination">
                <span className="sp-pagination-label">Page {page} of {lastPage}</span>
                <div className="sp-pagination-btns">
                  <button
                    className="sp-pagination-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    className="sp-pagination-btn"
                    onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                    disabled={page >= lastPage}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Ticket detail modal */}
      {selectedId && (
        <div className="sp-modal-overlay" onClick={() => setSelectedId(null)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">
                {selectedTicket ? selectedTicket.subject : "Loading…"}
              </h2>
              <button className="btn-icon" onClick={() => setSelectedId(null)}>
                <X size={18} />
              </button>
            </div>

            {!selectedTicket ? (
              <div className="sp-empty" style={{ color: "var(--gray-400)" }}>Loading…</div>
            ) : (
              <>
                <div className="sp-modal-meta">
                  {selectedTicket.ticket_number && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="sp-modal-meta-label">Ticket #:</span>
                      <span className="sp-modal-meta-ticket">{selectedTicket.ticket_number}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="sp-modal-meta-label">Date:</span>
                    <span style={{ color: "#374151" }}>
                      {new Date(selectedTicket.created_at).toLocaleString("en-CA")}
                    </span>
                  </div>
                </div>

                <div
                  className="sp-modal-body"
                  dangerouslySetInnerHTML={{ __html: selectedTicket.body }}
                />

                {selectedTicket.attachments?.length > 0 && (
                  <div className="sp-modal-attachments">
                    <div className="sp-modal-attachments-title">
                      Attachments ({selectedTicket.attachments.length})
                    </div>
                    <div className="sp-modal-attachments-list">
                      {selectedTicket.attachments.map((url, idx) => (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 12, color: "var(--primary)", textDecoration: "underline", wordBreak: "break-all" }}
                        >
                          Attachment {idx + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
